// =========================================================================
// Job cards — projection only. Fork-owned (zone C), P5b lane.
//
// Port of the demo's `renderJobcards` (source line 2764) and of `JOB_TOOLS`
// (source line 2761), which is carried here as a module-local seed: it is a
// job-card concern and nothing else in the port reads it.
//
// The demo rendered one job at a time behind a list. The port shows every open
// technician job as a card, grouped by site, because the interesting question
// on this page is "what is my crew doing this week" and a list-plus-detail
// makes that thirteen clicks. The card's own content is the demo's, block for
// block: ranked causes from the fleet's work-order history, the controlled
// procedure, the tools for that system, the parts, the labour and the quote.
//
// Transformation layer (layer 3). No React, no fetching, no mutation.
//
// @see docs/logic/workflows/job-cards.md
// =========================================================================

import { BY_ID, MINES, RATES } from '../../data/core';
import type { MineCode } from '../../schema/machine';
import type {
  CataloguePart,
  InventoryLine,
  LibraryDocument,
  TechnicianJob,
  WorkOrder,
} from '../../shared/useOemData';
import { causesFor, fmt$, procFor, type FailureCauseRow } from '../../shared/oem-engine';

// -------------------------------------------------------------------------
// Seeds local to this surface
// -------------------------------------------------------------------------

/**
 * Demo line 2761. `JOB_TOOLS` — the tool list for a system, matched against
 * the component plus the technician's findings. Ordered: the first pattern
 * that matches wins, exactly as the demo's `find` takes it.
 */
export const JOB_TOOLS: readonly (readonly [RegExp, readonly string[]])[] = [
  [
    /brake|retarder/i,
    [
      'Brake pad gauge',
      'IR thermometer / thermal camera',
      'Torque wrench 200–800 N·m',
      'Wheel chocks, jack stands',
      'Retarder valve test kit',
    ],
  ],
  [
    /hydraulic|coupling|pump/i,
    [
      '3 µm filter cart + two drums',
      'Particle counter / sample kit',
      'Torque wrench 50–300 N·m',
      'Coupling alignment gauge',
      'Catch tray, absorbents',
    ],
  ],
  [
    /rock drill|feed|boom/i,
    [
      'Front-end puller',
      'Torque wrench 420 N·m',
      'Accumulator charging kit (N₂)',
      'Boom stands',
      'Shank alignment gauge',
    ],
  ],
  [
    /dth|hammer|pipe|string|rotary|carousel/i,
    [
      'Breakout wrench with torque readout',
      'Thread gauges go/no-go (API reg)',
      'Ultrasonic wall gauge',
      'Thread compound',
      'Overshot (fishing) on the truck',
    ],
  ],
  [
    /engine|turbo|boost/i,
    [
      'Boost / exhaust temperature gauge',
      'Thermal camera',
      'Torque wrench',
      'Lifting gear 250 kg',
      'Harness continuity tester',
    ],
  ],
  [
    /battery|hv|isolation|charging/i,
    [
      'HV isolation test kit (1,000 V)',
      'Insulated tools, HV gloves',
      'Module lifting frame',
      'Coolant flow gauge',
    ],
  ],
  [
    /track|undercarriage|roller/i,
    ['Track press / pin tool', 'Tension gauge', 'Roller bearing kit', 'Lifting gear 2 t'],
  ],
  [
    /dust|collector|compressor/i,
    ['Vacuum gauge', 'Pulse-valve test lead', 'Separator wrench', 'Air pressure gauge'],
  ],
];

/** The demo's fallback when no pattern matches. */
export const DEFAULT_TOOLS: readonly string[] = [
  'Standard fitter kit',
  'Torque wrench',
  'Lifting gear',
  'Isolation locks and tags',
];

/**
 * Demo line 2767. The parts-cost uplift and the delivery charge per site.
 *
 * The demo wrote five sites into each map and left **Mount Read out of both**,
 * so an MR job priced as `NaN` there. The port keeps the five it priced and
 * treats a missing site as unpriced rather than inventing a sixth figure —
 * the card then says the parts uplift and freight are not priced for that
 * site instead of printing a number nobody wrote.
 */
const PARTS_UPLIFT: Readonly<Partial<Record<MineCode, number>>> = {
  KG: 0.06,
  BR: 0.09,
  MI: 0.11,
  OH: 0.12,
  GD: 0.08,
};

const DELIVERY_AUD: Readonly<Partial<Record<MineCode, number>>> = {
  KG: 120,
  BR: 260,
  MI: 340,
  OH: 480,
  GD: 220,
};

/**
 * Demo line 2775. The checklist the demo falls back to.
 *
 * It is always the fallback here: `checklist` is not a field on
 * `TechnicianJob` in the extracted pack, so the demo's `j.checklist || […]`
 * always took the right-hand branch too.
 */
export const defaultChecklist = (completed: boolean) => [
  { item: 'Isolation verified (LOTO-UG-003)', result: 'Pass' },
  { item: 'Procedure attached', result: 'Pass' },
  { item: 'Post-repair test', result: completed ? 'Pass' : 'n/a' },
];

// -------------------------------------------------------------------------
// The data contract
// -------------------------------------------------------------------------

export interface JobCardsCtx {
  readonly techJobs: readonly TechnicianJob[];
  readonly workOrders: readonly WorkOrder[];
  readonly parts: readonly CataloguePart[];
  readonly documents: readonly LibraryDocument[];
}

// -------------------------------------------------------------------------
// One card
// -------------------------------------------------------------------------

/** One priced line on the quote. */
export interface QuoteLine {
  readonly label: string;
  /** Already formatted, or `null` when the site was never priced. */
  readonly amount: string | null;
}

/** One part on the job, joined to the catalogue. */
export interface JobPartLine {
  readonly part_no: string;
  readonly description: string;
  readonly qty: number;
  readonly amount: string | null;
  readonly criticality: string;
}

/** Everything one job card shows. */
export interface JobCardModel {
  readonly job: TechnicianJob;
  readonly mine: MineCode;
  readonly siteName: string;
  readonly model: string;
  /** Component from the linked work order, `''` when there is none. */
  readonly component: string;
  /** Technician's findings, falling back to the work-order description. */
  readonly findings: string;
  /** Ranked causes across the fleet's corrective history for this component. */
  readonly causes: readonly FailureCauseRow[];
  /** How many orders the ranking was drawn from — the demo prints it. */
  readonly causeBase: number;
  /** The controlled procedure, or `undefined`. */
  readonly procedure: LibraryDocument | undefined;
  readonly tools: readonly string[];
  readonly parts: readonly JobPartLine[];
  readonly labourHours: number;
  readonly labourRate: number;
  readonly quote: readonly QuoteLine[];
  readonly checklist: readonly { item: string; result: string }[];
  /** `null` until the job is completed. */
  readonly actual: string | null;
  /** Lost production beside the actual — the demo's `+ A$… lost production`. */
  readonly actualDowntime: string | null;
  /**
   * The linked work order's id when it is in the ledger, else `null`. The WO
   * number and the actual open the ledger row; with no row there is nothing
   * to open, so the scene prints the reference as plain text.
   */
  readonly woId: string | null;
}

/** Demo line 2766. Everything one card needs, for one technician job. */
export function jobCardModel(job: TechnicianJob, ctx: JobCardsCtx): JobCardModel {
  const fleet = BY_ID[job.machine];
  const mine = fleet.mine;
  const wo = ctx.workOrders.find((w) => w.wo_id === job.wo_ref);
  const component = wo?.component ?? '';

  const causes = component ? causesFor(component, ctx) : [];
  const causeBase = component
    ? ctx.workOrders.filter((w) => w.component === component && w.wo_type !== 'Preventive')
        .length
    : 0;

  const toolMatch = JOB_TOOLS.find((t) => t[0].test(`${component} ${job.findings}`));
  const tools = toolMatch ? toolMatch[1] : DEFAULT_TOOLS;

  const rate = RATES[mine].labour;
  const labourHours = job.labour_lines.length
    ? job.labour_lines.reduce((a, l) => a + l.hours, 0)
    : (wo?.labour_h ?? 4);

  const uplift = PARTS_UPLIFT[mine];
  const delivery = DELIVERY_AUD[mine];
  const priced = uplift !== undefined && delivery !== undefined;

  const parts: JobPartLine[] = job.parts_used.map((p) => {
    const cat = ctx.parts.find((c) => c.part_no === p.part_no);
    return {
      part_no: p.part_no,
      description: cat?.description ?? '',
      qty: p.qty,
      amount: cat ? fmt$(cat.list_price_aud * p.qty) : null,
      criticality: cat?.criticality ?? '',
    };
  });

  const partsList = job.parts_used.reduce((a, p) => {
    const cat = ctx.parts.find((c) => c.part_no === p.part_no);
    return a + (cat ? cat.list_price_aud * p.qty : 0);
  }, 0);
  const partsCost = priced ? partsList * (1 + (uplift as number)) : partsList;
  const freight = parts.length && priced ? (delivery as number) : 0;
  const labourCost = labourHours * rate;

  const quote: QuoteLine[] = [
    {
      label: `Labour ${labourHours.toFixed(1)} h × A$${rate}/h (${MINES[mine].name})`,
      amount: fmt$(labourCost),
    },
    ...parts.map((p) => ({
      label: `${p.part_no} · ${p.description} × ${p.qty}`,
      amount: p.amount,
    })),
  ];

  if (parts.length) {
    quote.push({
      label: `Freight uplift + delivery from ${RATES[mine].dc}`,
      amount: priced ? fmt$(partsCost - partsList + freight) : null,
    });
  }

  // On an unpriced site the labour and the parts at list are still known, so
  // the total is given and the label says what is missing from it. Printing
  // nothing would throw away two thirds of a figure the data does support.
  quote.push({
    label: priced ? 'Quote' : 'Quote · excludes freight, not priced for this site',
    amount: fmt$(labourCost + partsCost + freight),
  });

  return {
    job,
    mine,
    siteName: MINES[mine].name,
    model: fleet.model,
    component,
    findings: job.findings || wo?.description || '',
    causes,
    causeBase,
    procedure: component ? procFor(component, ctx) : undefined,
    tools,
    parts,
    labourHours,
    labourRate: rate,
    quote,
    checklist: defaultChecklist(job.status === 'Completed'),
    actual:
      wo && job.status === 'Completed' ? fmt$(wo.total_direct_cost_aud) : null,
    actualDowntime:
      wo && job.status === 'Completed' && wo.downtime_cost_aud
        ? fmt$(wo.downtime_cost_aud)
        : null,
    woId: wo?.wo_id ?? null,
  };
}

// -------------------------------------------------------------------------
// Grouping and filtering
// -------------------------------------------------------------------------

/** The demo's `JOB_FILT`, as URL state. */
export interface JobCardFilters {
  /** `?site=KG`. */
  readonly mine: MineCode | null;
  /** `?status=Scheduled`. */
  readonly status: string | null;
  /** `?open=0` shows completed jobs too. */
  readonly openOnly: boolean;
}

export const TECH_JOB_STATUSES = ['Scheduled', 'In progress', 'Completed'] as const;

/** One site's cards. */
export interface JobCardGroup {
  readonly mine: MineCode;
  readonly siteName: string;
  readonly cards: readonly JobCardModel[];
}

/**
 * Cards grouped by site, in fleet order, most recently on site first inside
 * each group (the demo's `b.on_site.localeCompare(a.on_site)`).
 */
export function jobCardGroups(
  ctx: JobCardsCtx,
  filters: JobCardFilters,
  order: readonly MineCode[],
): JobCardGroup[] {
  const selected = ctx.techJobs
    .filter((j) => BY_ID[j.machine])
    .filter((j) => (filters.openOnly ? j.status !== 'Completed' : true))
    .filter((j) => (filters.status ? j.status === filters.status : true))
    .filter((j) => (filters.mine ? BY_ID[j.machine].mine === filters.mine : true))
    .slice()
    .sort((a, b) => b.on_site.localeCompare(a.on_site));

  return order
    .map((mine) => ({
      mine,
      siteName: MINES[mine].name,
      cards: selected
        .filter((j) => BY_ID[j.machine].mine === mine)
        .map((j) => jobCardModel(j, ctx)),
    }))
    .filter((g) => g.cards.length > 0);
}

/** Head figures — the demo's `<span class="pill">` beside the title. */
export interface JobCardsSummary {
  readonly total: number;
  readonly open: number;
  readonly shown: number;
  readonly sites: number;
  readonly technicians: number;
}

export function jobCardsSummary(
  ctx: JobCardsCtx,
  groups: readonly JobCardGroup[],
): JobCardsSummary {
  const shown = groups.flatMap((g) => g.cards);
  return {
    total: ctx.techJobs.length,
    open: ctx.techJobs.filter((j) => j.status !== 'Completed').length,
    shown: shown.length,
    sites: groups.length,
    technicians: new Set(shown.map((c) => c.job.technician)).size,
  };
}

// -------------------------------------------------------------------------
// Source slices — the demo's `FIG(value, table, { key })` on a card
// -------------------------------------------------------------------------

/**
 * The ledger row behind a card's WO number and its actual cost — the demo's
 * `FIG(j.wo_ref, 'wos', { wo_id })` and `FIG(fmt$(…), 'wos', { wo_id })`.
 */
export const workOrderSlice = (
  workOrders: readonly WorkOrder[],
  woId: string,
): WorkOrder[] => workOrders.filter((w) => w.wo_id === woId);

/**
 * Every stock line for one part across the stores — the demo's
 * `FIG(p.part_no, 'inv', { part_no })`.
 */
export const inventorySlice = (
  inventory: readonly InventoryLine[],
  partNo: string,
): InventoryLine[] => inventory.filter((i) => i.part_no === partNo);
